import React from 'react';
import { cn } from '../../lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

interface AssistantPanelShellProps {
  isOpen: boolean;
  onOpen: () => void;
  toggleLabel: string;
  toggleAriaLabel: string;
  toggleTooltip?: string;
  toggleIcon: React.ReactNode;
  toggleTestId?: string;
  panelAriaLabel: string;
  /** Stable marker on the open panel root, so canvas fitters can measure the
   *  region it occludes and center content in the visible area. */
  panelTestId?: string;
  className?: string;
  panelClassName?: string;
  children: React.ReactNode;
}

export const AssistantPanelShell: React.FC<AssistantPanelShellProps> = ({
  isOpen,
  onOpen,
  toggleLabel,
  toggleAriaLabel,
  toggleTooltip,
  toggleIcon,
  toggleTestId,
  panelAriaLabel,
  panelTestId,
  className,
  panelClassName,
  children,
}) => {
  if (!isOpen) {
    const collapsedToggle = (
      <button
        data-testid={toggleTestId}
        aria-label={toggleAriaLabel}
        title={toggleTooltip ?? toggleAriaLabel}
        onClick={onOpen}
        className={cn(
          'absolute left-0 top-1/3 h-[42px] min-w-[48px] max-w-[120px] pl-2 pr-2.5 flex items-center gap-1.5 overflow-hidden bg-gradient-to-br from-blue-500 to-blue-600 rounded-tr-lg rounded-br-lg shadow-[0px_1px_4px_0px_rgba(0,0,0,0.4)] border-[0.6px] border-white/40 text-white cursor-pointer',
          className
        )}
      >
        {toggleIcon}
        <span className="whitespace-nowrap text-xs font-semibold leading-none truncate">
          {toggleLabel}
        </span>
      </button>
    );

    if (toggleTooltip) {
      return (
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>{collapsedToggle}</TooltipTrigger>
            <TooltipContent side="right">{toggleTooltip}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return collapsedToggle;
  }

  return (
    <div
      className={cn(
        'absolute left-6 top-24 z-40 rounded-lg border bg-background shadow-md overflow-hidden flex flex-col',
        'animate-in slide-in-from-left-5 duration-300',
        panelClassName,
        className
      )}
      role="complementary"
      aria-label={panelAriaLabel}
      data-testid={panelTestId}
    >
      {children}
    </div>
  );
};
